import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ClipboardCheck, GitBranch, Hand, MinusCircle, Paperclip, Play, Square, UserPlus, Users, X, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { BOOKING_FILES, DEFAULT_BLOCKS, DELIVERY_OPTIONS, FILE_WORDS, TEMPLATE_FILE, TRIGGERS } from "./StageDetail";
import { duration, onEngine, quoted, ruleLine, testRun, wordOf } from "./journeyCheck";
import { BUILT_IN_RULES, rowPhases } from "./builtInRules";

// The whole Service journey as a graph, drawn from the editor's current (unsaved too) rows: one
// lane per phase, one small node per stage, and an arrow from each stage to the next (solid when
// it follows on its own, dashed when someone or something else starts it). Clicking a node shows
// its details beside the graph. Test run walks a made-up booking through the journey
// (journeyCheck.js) and lists what would stop or fail.

// One stage on the stage engine: the same settings the stage form edits.
function engineStage(row, prev, pick, usable) {
  const b = { ...DEFAULT_BLOCKS, ...(row.blocks || {}) };
  const approved = usable.filter((t) => t.status === "approved");
  const template = usable.find((t) => t.id === pick?.id);
  const text = b.message_type === "text";
  const attachment = text ? (b.attachment === "album" ? "album" : "none") : template ? TEMPLATE_FILE[template.template_type] || "none" : b.attachment;
  const start =
    b.trigger === "automatic" ? (prev ? `When ${prev.label} is done` : "Automatic") : b.trigger === "manual" ? "Advisor sends it" : wordOf(TRIGGERS, b.trigger);
  const facts = [
    ["Message", text ? (b.text || "").trim() || "No text yet" : template?.name || "No template chosen", text ? !(b.text || "").trim() : !template],
    ["Send to", text || b.recipient !== "customer" ? "Group" : "Customer only"],
  ];
  if (attachment !== "none") {
    facts.push(["File", `${FILE_WORDS[attachment]}${b.booking_file ? ` · ${wordOf(BOOKING_FILES, b.booking_file)} from booking form` : " · advisor adds it"}`]);
  }
  if (b.only_if?.field) {
    const value = b.only_if.field === "delivery_option" ? wordOf(DELIVERY_OPTIONS, b.only_if.value) : b.only_if.value;
    facts.push(["Only if", `${b.only_if.field === "delivery_option" ? "Delivery option" : "Service type"} is ${value || "…"}`]);
  }
  if (b.delete_group_minutes != null) facts.push(["Then", `Deletes the group after ${duration(b.delete_group_minutes * 60)}`]);
  const rules = (b.capture_rules || []).map((r) => ruleLine(r, approved));
  return {
    start,
    kind: b.trigger,
    wait: b.trigger !== "manual" && b.delay_seconds > 0 ? duration(b.delay_seconds) : "",
    manual: b.trigger === "manual",
    facts,
    rules,
    done: rules.some((r) => r.done) ? "Done by a Mark done rule" : "Done when sent",
  };
}

// A built-in stage on the older code: what its code does, from builtInRules and its messages.
function builtInStage(row, templates) {
  const rules = BUILT_IN_RULES[row.stage_key] || {};
  const own = templates.filter((t) => t.stage === row.stage_key);
  const facts = own.map((t) => {
    const on = t.enabled == null ? t.default_enabled : t.enabled;
    const name = t.send_mode === "direct" ? "Text message" : t.template_name || t.platform_default || "No template chosen";
    return [own.length > 1 ? t.label : "Message", on ? name : `${name} (off)`];
  });
  if (!own.length) facts.push(["Message", "None"]);
  else if (rules.recipient === "Set below") facts.push(["Send to", own[0].config?.channel === "one_on_one" ? "Customer only" : "Group"]);
  else facts.push(["Send to", rules.recipient || "Group"]);
  if (rules.attachment) facts.push(["File", rules.attachment]);
  const replies = (rules.replies || []).flatMap(([button, status, , next]) => [
    ...(status !== "—" ? [{ when: `Customer taps ${quoted(button.split(", "))}`, then: `Set status: ${status}` }] : []),
    ...(next !== "Stay" ? [{ when: `Customer taps ${quoted(button.split(", "))}`, then: `Starts ${next}` }] : []),
  ]);
  if (row.capture_from_whatsapp && row.keywordsText) {
    replies.push({ when: `Staff writes ${quoted(row.keywordsText.split("\n").filter(Boolean))}`, then: "Mark done", done: true });
  }
  const trigger = rules.trigger || "Manual";
  return {
    start: trigger,
    kind: trigger.startsWith("Manual") ? "manual" : { "Customer joined": "customer_joined", "Group created": "group_created", "Booking saved": "booking" }[trigger] || "automatic",
    wait: typeof row.auto_send_delay_seconds === "number" && row.auto_send_delay_seconds > 0 ? duration(row.auto_send_delay_seconds) : "",
    manual: trigger.startsWith("Manual"),
    facts,
    rules: replies,
    done: { "On group created": "Done when the group is created", "On reply": "Done when the customer replies" }[rules.completion] || "Done when sent",
  };
}

// How each stage starts, as the small tag on its node.
const START_TAGS = {
  automatic: [Zap, "Auto"],
  manual: [Hand, "Manual"],
  customer_joined: [UserPlus, "Customer joined"],
  group_created: [Users, "Group created"],
  booking: [ClipboardCheck, "Booking saved"],
};

const short = (wait) => wait.replace(/ (min|sec)$/, (m, u) => u[0]).replace(/ hours?$/, "h").replace(/ days?$/, "d");

function Node({ row, index, stage, check, ran, selected, active, visited, onSelect, nodeRef }) {
  const [Icon, tag] = START_TAGS[stage.kind] || START_TAGS.automatic;
  const hasFile = stage.facts.some(([k]) => k === "File");
  return (
    <button
      ref={nodeRef}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-stage={row.stage_key}
      style={{ animationDelay: `${index * 35}ms` }}
      className={`jp-node w-full cursor-pointer rounded-xl border bg-white px-3 py-[9px] text-left transition-[border-color,box-shadow,transform,background-color] duration-200 motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2 ${
        active
          ? "scale-[1.04] border-[#2667B5] bg-[#F5F9FF] shadow-[0_0_0_4px_rgba(38,103,181,0.16),0_8px_20px_-8px_rgba(38,103,181,0.45)]"
          : selected
            ? "border-[#2667B5] shadow-[0_0_0_3px_rgba(38,103,181,0.14)]"
            : ran && check === "error"
              ? "border-[#FDA29B] bg-[#FFFBFA] shadow-[0_0_0_3px_rgba(240,68,56,0.10)]"
              : ran && check === "warn"
                ? "border-[#FEC84B] bg-[#FFFCF5]"
                : `shadow-[0_1px_2px_rgba(16,24,40,0.06)] hover:-translate-y-px hover:border-[#98A2B3] hover:shadow-[0_6px_14px_-6px_rgba(16,24,40,0.18)] ${row.is_visible ? "border-[#D0D5DD]" : "border-dashed border-[#D0D5DD]"}`
      } ${row.is_visible ? "" : "opacity-75"}`}
    >
      <span className="flex items-center gap-2">
        <span
          className={`flex h-5 w-5 flex-none items-center justify-center text-[10.5px] font-semibold tabular-nums transition-colors duration-200 ${row.is_custom ? "rounded-[6px]" : "rounded-full"} ${
            active || visited || selected ? "bg-[#2667B5] text-white" : "bg-[#F2F4F7] text-[#475467]"
          }`}
        >
          {index + 1}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#101828]" title={row.label}>
          {row.label}
        </span>
        {(check === "error" || check === "warn") && (
          <span className={`h-[7px] w-[7px] flex-none rounded-full ${check === "error" ? "bg-[#F04438]" : "bg-[#F79009]"}`} title={check === "error" ? "Has a problem" : "Has a warning"} />
        )}
        {(check === "error" || check === "warn") && (
          <span className="sr-only">{check === "error" ? ", has a problem" : ", has a warning"}</span>
        )}
      </span>
      <span className="mt-[6px] flex items-center gap-[6px] text-[11.5px] text-[#667085]">
        <Icon size={12} className={`flex-none ${stage.kind === "automatic" ? "text-[#2667B5]" : "text-[#98A2B3]"}`} aria-hidden="true" />
        <span className="truncate">
          {row.is_visible ? tag : `Hidden · ${tag}`}
          {stage.wait && ` · ${short(stage.wait)}`}
        </span>
        <span className="ml-auto flex flex-none items-center gap-[6px] text-[#667085]">
          {hasFile && <Paperclip size={12} aria-label="Sends a file" />}
          {stage.rules.length > 0 && (
            <span className="inline-flex items-center gap-[2px]" aria-label={`${stage.rules.length} rules`}>
              <GitBranch size={12} aria-hidden="true" />
              <span className="tabular-nums">{stage.rules.length}</span>
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

// An arrow from one node to the next: down inside a lane, across between lanes.
function edgePath(a, b) {
  if (Math.abs(a.x - b.x) < 4 && b.y > a.y) {
    const x = a.x + a.w / 2;
    return `M ${x} ${a.y + a.h} L ${x} ${b.y - 2}`;
  }
  if (b.x > a.x + a.w) {
    const [x1, y1, x2, y2] = [a.x + a.w, a.y + a.h / 2, b.x - 2, b.y + b.h / 2];
    const dx = (x2 - x1) / 2;
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }
  const [x1, y1, x2, y2] = [a.x + a.w / 2, a.y + a.h, b.x + b.w / 2, b.y - 2];
  return `M ${x1} ${y1} C ${x1} ${y1 + 48}, ${x2} ${y2 - 48}, ${x2} ${y2}`;
}

// The dot that runs along an arrow while the journey plays.
function Traveller({ d }) {
  const motion = useRef(null);
  useEffect(() => {
    motion.current?.beginElement?.();
  }, [d]);
  return (
    <circle r="4.5" fill="#2667B5" stroke="white" strokeWidth="2">
      <animateMotion ref={motion} dur="0.6s" begin="indefinite" fill="freeze" path={d} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
    </circle>
  );
}

// The panel beside the graph: a header with a close button, then the body.
function SidePanel({ label, closeLabel, onClose, header, children }) {
  return (
    <aside
      aria-label={label}
      className="absolute inset-x-3 bottom-3 z-10 flex max-h-[62%] flex-col overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_20px_40px_-12px_rgba(16,24,40,0.25)] [animation:isd-slide_220ms_ease-out] motion-reduce:[animation:none] md:inset-x-auto md:bottom-4 md:right-4 md:top-4 md:max-h-none md:w-[340px]"
    >
      <div className="flex items-start gap-3 border-b border-[#EAECF0] px-5 py-4">
        {header}
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg text-[#667085] transition-colors duration-150 hover:bg-[#F2F4F7] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {children}
    </aside>
  );
}

function Details({ row, index, stage, step, onClose, onEdit }) {
  return (
    <SidePanel
      label={`${row.label} details`}
      closeLabel="Close details"
      onClose={onClose}
      header={
        <>
          <span className={`mt-[2px] flex h-6 w-6 flex-none items-center justify-center bg-[#2667B5] text-[11.5px] font-semibold tabular-nums text-white ${row.is_custom ? "rounded-[7px]" : "rounded-full"}`}>
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-[#101828]">{row.label}</div>
            <div className="mt-[2px] text-[13px] text-[#667085]">
              {stage.start}
              {stage.wait && ` · wait ${stage.wait}`}
            </div>
          </div>
        </>
      }
    >
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {step && step.lines.some((l) => l.level === "error" || l.level === "warn") && (
          <ul className="m-0 mb-4 flex list-none flex-col gap-[6px] rounded-lg bg-[#FFFBFA] p-3 ring-1 ring-[#FEE4E2]">
            {step.lines
              .filter((l) => l.level === "error" || l.level === "warn")
              .map((l, i) => (
                <TestLine key={i} line={l} />
              ))}
          </ul>
        )}
        <dl className="m-0 grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-2 text-[13px]">
          {stage.facts.map(([k, v, missing]) => (
            <div key={k} className="contents">
              <dt className="text-[#667085]">{k}</dt>
              <dd className={`m-0 [overflow-wrap:anywhere] ${missing ? "text-[#B54708]" : "text-[#344054]"}`}>{v}</dd>
            </div>
          ))}
        </dl>
        {stage.rules.length > 0 && (
          <ul className="m-0 mt-4 flex list-none flex-col gap-2 border-t border-[#EAECF0] p-0 pt-4">
            {stage.rules.map((r, i) => (
              <li key={i} className="rounded-lg bg-[#F9FAFB] px-3 py-2 text-[13px] text-[#344054]">
                {r.when} <span className="text-[#98A2B3]">→</span> <span className={`font-medium ${r.done ? "text-[#067647]" : "text-[#1F5596]"}`}>{r.then}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex items-center gap-[6px] text-[12.5px] text-[#667085]">
          <CheckCircle2 size={13} className="flex-none text-[#12B76A]" aria-hidden="true" />
          {stage.done}
        </div>
      </div>
      <div className="border-t border-[#EAECF0] px-5 py-3">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-lg bg-[#2667B5] text-[13.5px] font-semibold text-white transition-colors duration-150 hover:bg-[#1F5596] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
        >
          Edit stage
        </button>
      </div>
    </SidePanel>
  );
}

function JourneyGraph({ rows, stages, steps, playAt, ranTo, selectedKey, onSelect }) {
  const wrap = useRef(null);
  const nodes = useRef({});
  const [box, setBox] = useState({ w: 0, h: 0, at: {} });
  // Node positions from offsets, not screen boxes, so the dialog's opening zoom does not skew them.
  useLayoutEffect(() => {
    const measure = () => {
      const at = {};
      Object.entries(nodes.current).forEach(([k, el]) => {
        if (el) at[k] = { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight };
      });
      setBox({ w: wrap.current.scrollWidth, h: wrap.current.scrollHeight, at });
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(wrap.current);
    return () => watch.disconnect();
  }, [rows]);

  // Arrow keys move between stages in journey order; Home and End jump to the first and last.
  const onKeyDown = (e) => {
    const at = rows.findIndex((r) => r.stage_key === e.target.dataset?.stage);
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (at < 0 || (step == null && e.key !== "Home" && e.key !== "End")) return;
    e.preventDefault();
    const to = e.key === "Home" ? 0 : e.key === "End" ? rows.length - 1 : Math.min(rows.length - 1, Math.max(0, at + step));
    nodes.current[rows[to].stage_key]?.focus();
  };

  const phases = rowPhases(rows);
  const lanes = [];
  rows.forEach((row, i) => {
    if (!lanes.length || lanes[lanes.length - 1].phase !== phases[i]) lanes.push({ phase: phases[i], items: [] });
    lanes[lanes.length - 1].items.push(i);
  });
  const edges = rows.slice(1).map((row, n) => {
    const a = box.at[rows[n].stage_key];
    const b = box.at[row.stage_key];
    return a && b ? { key: row.stage_key, i: n + 1, d: edgePath(a, b), dashed: stages[n + 1].kind !== "automatic" } : null;
  });
  const COLOURS = { blue: "#2667B5", light: "#C1C7D0", grey: "#98A2B3" };

  return (
    <div
      ref={wrap}
      role="group"
      aria-label="Stages. Arrow keys move between them."
      onKeyDown={onKeyDown}
      className="relative mx-auto flex flex-wrap items-start justify-center gap-x-9 gap-y-8 px-4 py-8 md:w-max md:flex-nowrap md:px-6 md:py-10"
    >
      <svg className="pointer-events-none absolute left-0 top-0 z-[1] overflow-visible" width={box.w} height={box.h} aria-hidden="true">
        <defs>
          {Object.entries(COLOURS).map(([id, fill]) => (
            <marker key={id} id={`jp-arrow-${id}`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0,0.5 L7.5,4 L0,7.5 z" fill={fill} />
            </marker>
          ))}
        </defs>
        {edges.map((e) => {
          if (!e) return null;
          const lit = playAt != null && e.i <= playAt && steps[e.i].level !== "error";
          const colour = lit ? "blue" : e.dashed ? "light" : "grey";
          return (
            <path
              key={e.key}
              d={e.d}
              pathLength={e.dashed ? undefined : 1}
              className={e.dashed ? "jp-edge-fade" : "jp-edge-draw"}
              style={{ animationDelay: `${150 + e.i * 35}ms` }}
              fill="none"
              stroke={COLOURS[colour]}
              strokeWidth={lit ? 2 : 1.5}
              strokeDasharray={e.dashed ? "4 4" : undefined}
              markerEnd={`url(#jp-arrow-${colour})`}
            />
          );
        })}
        {playAt > 0 && edges[playAt - 1] && <Traveller key={playAt} d={edges[playAt - 1].d} />}
      </svg>
      {lanes.map((lane) => (
        <section key={lane.phase} aria-label={lane.phase} data-phase={lane.phase} className="w-[176px] flex-none rounded-2xl bg-[#F2F4F7]/70 px-[6px] pb-[6px] pt-2">
          <h3 className="m-0 flex items-baseline justify-between px-2 pb-2 text-[12px] font-semibold text-[#475467]">
            {lane.phase}
            <span className="font-medium tabular-nums text-[#667085]">{lane.items.length}</span>
          </h3>
          <div className="flex flex-col gap-4">
            {lane.items.map((i) => (
              <Node
                key={rows[i].stage_key}
                nodeRef={(el) => {
                  nodes.current[rows[i].stage_key] = el;
                }}
                row={rows[i]}
                index={i}
                stage={stages[i]}
                check={steps[i].level}
                ran={i <= ranTo}
                selected={selectedKey === rows[i].stage_key}
                active={playAt === i}
                visited={playAt != null && i < playAt}
                onSelect={() => onSelect(selectedKey === rows[i].stage_key ? null : rows[i].stage_key)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const LINE_LOOK = {
  ok: [CheckCircle2, "text-[#12B76A]", "text-[#344054]"],
  warn: [AlertTriangle, "text-[#F79009]", "text-[#93370D]"],
  error: [AlertCircle, "text-[#F04438]", "text-[#912018]"],
  skip: [MinusCircle, "text-[#98A2B3]", "text-[#667085]"],
};

function TestLine({ line }) {
  const [Icon, iconColour, textColour] = LINE_LOOK[line.level] || LINE_LOOK.ok;
  return (
    <li className={`flex items-start gap-2 text-[12.5px] leading-[18px] ${textColour}`}>
      <Icon size={13} className={`mt-[2px] flex-none ${iconColour}`} aria-hidden="true" />
      <span className="min-w-0 [overflow-wrap:anywhere]">{line.text}</span>
    </li>
  );
}

// The test run's results, stage by stage, filling in as the run moves down the journey.
function TestPanel({ steps, shown, running, onlyProblems, onOnlyProblems, onPick, onClose }) {
  const end = useRef(null);
  useEffect(() => {
    if (running) end.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [shown, running]);
  const errors = steps.filter((s) => s.level === "error").length;
  const warnings = steps.filter((s) => s.level === "warn").length;
  const summary = running
    ? `Running: stage ${shown} of ${steps.length}`
    : errors + warnings === 0
      ? "No problems found"
      : [errors && `${errors} ${errors === 1 ? "stage has a problem" : "stages have problems"}`, warnings && `${warnings} with ${warnings === 1 ? "a warning" : "warnings"}`].filter(Boolean).join(", ");
  return (
    <SidePanel
      label="Test run"
      closeLabel="Close test run"
      onClose={onClose}
      header={
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-[#101828]">Test run</div>
          <div
            aria-live={running ? "off" : "polite"}
            className={`mt-[2px] text-[13px] font-medium ${running ? "text-[#667085]" : errors ? "text-[#D92D20]" : warnings ? "text-[#B54708]" : "text-[#067647]"}`}
          >
            {summary}
          </div>
          {!running && errors + warnings > 0 && (
            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-[12.5px] text-[#475467]">
              <input type="checkbox" checked={onlyProblems} onChange={(e) => onOnlyProblems(e.target.checked)} className="h-[14px] w-[14px] accent-[#2667B5]" />
              Problems and warnings only
            </label>
          )}
        </div>
      }
    >
      <ol className="m-0 flex-1 list-none overflow-y-auto px-3 py-3">
        {steps.slice(0, shown).map((step, i) =>
          !running && onlyProblems && step.level !== "error" && step.level !== "warn" ? null : (
            <li key={step.key} className="jp-step">
              <button
                type="button"
                onClick={() => onPick(step.key)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-[6px] text-left transition-colors duration-150 hover:bg-[#F9FAFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
              >
                <span
                  className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[10.5px] font-semibold tabular-nums ${
                    { error: "bg-[#FEE4E2] text-[#B42318]", warn: "bg-[#FEF0C7] text-[#B54708]", skip: "bg-[#F2F4F7] text-[#667085]" }[step.level] || "bg-[#DCFAE6] text-[#067647]"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="truncate text-[13px] font-semibold text-[#101828]">{step.label}</span>
              </button>
              <ul className="m-0 mb-2 ml-[37px] flex list-none flex-col gap-1 p-0 pr-2">
                {step.lines.map((l, n) => (
                  <TestLine key={n} line={l} />
                ))}
              </ul>
            </li>
          ),
        )}
        <li ref={end} aria-hidden="true" />
      </ol>
    </SidePanel>
  );
}

export default function JourneyPreview({ open, onOpenChange, rows, engine, picks, usable, templates, onOpenStage }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [playAt, setPlayAt] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  useEffect(() => {
    if (!open) {
      setSelectedKey(null);
      setPlayAt(null);
      setTestOpen(false);
    }
  }, [open]);
  // The test run moves one stage at a time, quickly, then stops after the last.
  useEffect(() => {
    if (playAt == null) return undefined;
    const t = setTimeout(() => setPlayAt(playAt + 1 < rows.length ? playAt + 1 : null), 420);
    return () => clearTimeout(t);
  }, [playAt, rows.length]);

  const stages = rows.map((row, i) =>
    onEngine(row, engine)
      ? engineStage(row, rows.slice(0, i).filter((r) => r.is_visible).pop(), picks[row.stage_key], usable)
      : builtInStage(row, templates),
  );
  const steps = testRun({ rows, engine, picks, usable });
  const problems = steps.filter((s) => s.level === "error").length;
  const warnings = steps.filter((s) => s.level === "warn").length;
  // One button per phase, with its stage count and whether any stage in it has a problem.
  const phaseList = [];
  rowPhases(rows).forEach((phase, i) => {
    if (phaseList[phaseList.length - 1]?.phase !== phase) phaseList.push({ phase, count: 0, problems: 0, warnings: 0 });
    const ph = phaseList[phaseList.length - 1];
    ph.count += 1;
    if (steps[i].level === "error") ph.problems += 1;
    if (steps[i].level === "warn") ph.warnings += 1;
  });
  const jumpTo = (phase) => {
    const smooth = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document.querySelector(`[data-phase="${phase}"]`)?.scrollIntoView?.({ inline: "start", block: "nearest", behavior: smooth ? "smooth" : "auto" });
  };
  const selectedIndex = rows.findIndex((r) => r.stage_key === selectedKey);
  const running = playAt != null;
  const ranTo = running ? playAt : testOpen ? rows.length : -1;
  const startTest = () => {
    setSelectedKey(null);
    setOnlyProblems(false);
    setTestOpen(true);
    setPlayAt(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? null : 0);
  };
  // A stage picked in the test results: show its details and bring its node into view.
  const pickStage = (key) => {
    closeTest();
    setSelectedKey(key);
    requestAnimationFrame(() => document.querySelector(`[data-stage="${key}"]`)?.scrollIntoView?.({ block: "nearest", inline: "nearest" }));
  };
  // The header badges: the test results with only the stages that need a look.
  const showResults = () => {
    setSelectedKey(null);
    setOnlyProblems(true);
    setPlayAt(null);
    setTestOpen(true);
  };
  const closeTest = () => {
    setTestOpen(false);
    setPlayAt(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        onEscapeKeyDown={(e) => {
          if (testOpen || selectedKey) {
            e.preventDefault();
            closeTest();
            setSelectedKey(null);
          }
        }}
        className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 bg-[#FCFCFD] p-0 sm:rounded-none [&>button:last-child]:hidden">
        <div className="flex flex-none items-center gap-3 border-b border-[#EAECF0] bg-white px-4 py-3 sm:px-6">
          <div className="mr-auto flex min-w-0 items-center gap-3 whitespace-nowrap">
            <DialogTitle className="m-0 text-[17px] font-semibold tracking-[-0.01em] text-[#101828]">Service journey</DialogTitle>
            <span className="hidden text-[13px] font-medium tabular-nums text-[#667085] sm:inline">{rows.length} stages</span>
            {problems > 0 && (
              <button
                type="button"
                onClick={showResults}
                className="inline-flex h-6 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-[#FEF3F2] px-2 text-[12px] font-semibold text-[#B42318] ring-1 ring-[#FECDCA] transition-colors duration-150 hover:bg-[#FEE4E2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
              >
                <AlertCircle size={12} aria-hidden="true" />
                {problems}<span className="hidden sm:inline">{problems === 1 ? " problem" : " problems"}</span>
              </button>
            )}
            {warnings > 0 && (
              <button
                type="button"
                onClick={showResults}
                className="inline-flex h-6 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-[#FFFAEB] px-2 text-[12px] font-semibold text-[#B54708] ring-1 ring-[#FEDF89] transition-colors duration-150 hover:bg-[#FEF0C7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
              >
                <AlertTriangle size={12} aria-hidden="true" />
                {warnings}<span className="hidden sm:inline">{warnings === 1 ? " warning" : " warnings"}</span>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => (running ? setPlayAt(null) : startTest())}
            disabled={!rows.length}
            className="inline-flex h-9 flex-none cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg bg-[#2667B5] px-3 text-[13.5px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors duration-150 hover:bg-[#1F5596] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
          >
            {running ? <Square size={13} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
            {running ? "Show results" : "Test run"}
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close preview"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-[#667085] transition-colors duration-150 hover:bg-[#F2F4F7] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {phaseList.length > 1 && (
          <nav aria-label="Phases" className="flex flex-none items-center gap-1 overflow-x-auto border-b border-[#EAECF0] bg-white px-3 py-[6px] sm:px-5">
            {phaseList.map((ph) => (
              <button
                key={ph.phase}
                type="button"
                onClick={() => jumpTo(ph.phase)}
                className="inline-flex h-7 flex-none cursor-pointer items-center gap-[6px] whitespace-nowrap rounded-md px-2 text-[12.5px] font-medium text-[#344054] transition-colors duration-150 hover:bg-[#F2F4F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-2"
              >
                {ph.phase}
                <span className="tabular-nums text-[#667085]">{ph.count}</span>
                {ph.problems > 0 && <span className="h-[7px] w-[7px] rounded-full bg-[#F04438]" aria-label={`${ph.problems} with problems`} />}
                {!ph.problems && ph.warnings > 0 && <span className="h-[7px] w-[7px] rounded-full bg-[#F79009]" aria-label={`${ph.warnings} with warnings`} />}
              </button>
            ))}
          </nav>
        )}
        <ul aria-label="Key" className="m-0 flex flex-none list-none flex-wrap items-center gap-x-5 gap-y-1 border-b border-[#EAECF0] bg-white px-4 py-2 text-[12px] text-[#475467] sm:px-6">
          <li className="flex items-center gap-[6px]">
            <svg width="22" height="6" aria-hidden="true"><path d="M1 3 H21" stroke="#98A2B3" strokeWidth="1.5" /></svg>Follows on its own
          </li>
          <li className="flex items-center gap-[6px]">
            <svg width="22" height="6" aria-hidden="true"><path d="M1 3 H21" stroke="#98A2B3" strokeWidth="1.5" strokeDasharray="4 4" /></svg>Someone starts it
          </li>
          <li className="flex items-center gap-[6px]">
            <span className="h-3 w-3 rounded-full bg-[#F2F4F7] ring-1 ring-[#D0D5DD]" aria-hidden="true" />Built-in stage
          </li>
          <li className="flex items-center gap-[6px]">
            <span className="h-3 w-3 rounded-[3px] bg-[#F2F4F7] ring-1 ring-[#D0D5DD]" aria-hidden="true" />Stage the brand added
          </li>
          <li className="flex items-center gap-[6px]">
            <GitBranch size={12} className="text-[#667085]" aria-hidden="true" />Rules
          </li>
          <li className="flex items-center gap-[6px]">
            <span className="h-[7px] w-[7px] rounded-full bg-[#F04438]" aria-hidden="true" />Problem
          </li>
          <li className="flex items-center gap-[6px]">
            <span className="h-[7px] w-[7px] rounded-full bg-[#F79009]" aria-hidden="true" />Warning
          </li>
        </ul>
        <div className="relative min-h-0 flex-1">
          {!rows.length && (
            <p className="absolute inset-0 z-[2] m-0 flex items-center justify-center px-6 text-center text-[14px] text-[#475467]">No stages yet. Close this and add one with Add stage.</p>
          )}
          <div className={`jp-canvas h-full overflow-auto transition-[padding] duration-200 ${testOpen || selectedIndex >= 0 ? "md:pr-[356px]" : ""}`}>
            <JourneyGraph
              rows={rows}
              stages={stages}
              steps={steps}
              playAt={playAt}
              ranTo={ranTo}
              selectedKey={selectedKey}
              onSelect={(key) => {
                closeTest();
                setSelectedKey(key);
              }}
            />
          </div>
          {testOpen ? (
            <TestPanel steps={steps} shown={running ? playAt + 1 : steps.length} running={running} onlyProblems={onlyProblems} onOnlyProblems={setOnlyProblems} onPick={pickStage} onClose={closeTest} />
          ) : (
            selectedIndex >= 0 && (
              <Details
                key={selectedKey}
                row={rows[selectedIndex]}
                index={selectedIndex}
                stage={stages[selectedIndex]}
                step={steps[selectedIndex]}
                onClose={() => setSelectedKey(null)}
                onEdit={() => onOpenStage(selectedKey)}
              />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
